import { Box, Typography, Paper, Chip } from '@mui/material';
import { Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';

interface PatientAISummaryPanelProps {
  aiSummary: string;
  isDark: boolean;
}

export function PatientAISummaryPanel({ aiSummary, isDark }: PatientAISummaryPanelProps) {
  const { t } = useTranslation('patients');
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3, mb: 3,
        border: '1px solid',
        borderColor: 'secondary.light',
        backgroundColor: isDark ? 'rgba(123, 31, 162, 0.05)' : 'rgba(123, 31, 162, 0.02)',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Sparkles size={20} color="#7b1fa2" />
        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7b1fa2', textTransform: 'uppercase', letterSpacing: 1.5 }}>
          {t('aiClinicalInsightsAndSummary')}
        </Typography>
        <Chip
          label={t('generativeAi')}
          size="small"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#7b1fa2', color: 'white', ml: 'auto' }}
        />
      </Box>

      <Box sx={{
        maxHeight: 500, overflowY: 'auto', pr: 1,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'secondary.light', borderRadius: 4 },
        '& p': { m: 0, mb: 1.5, fontSize: '0.92rem', lineHeight: 1.6, color: 'text.secondary' },
        '& ul, & ol': { m: 0, mb: 2, pl: 2.5, listStyleType: "'• '" },
        '& li': { mb: 0.75, fontSize: '0.92rem', color: 'text.primary', fontWeight: 500 },
        '& strong': { color: 'secondary.dark', fontWeight: 650 },
        '& h1, & h2, & h3': {
          color: 'secondary.main', mt: 3, mb: 1.5, fontWeight: 800, fontSize: '0.9rem',
          textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center',
          '&:after': { content: '""', flex: 1, height: '1px', bgcolor: 'divider', ml: 2 },
        },
        '& hr': { my: 2, border: 0, borderTop: '1px solid', borderColor: 'divider', opacity: 0.5 },
      }}>
        <ReactMarkdown>{aiSummary}</ReactMarkdown>
      </Box>

      <Box sx={{ position: 'absolute', bottom: -20, right: -20, opacity: 0.05, transform: 'rotate(-15deg)', pointerEvents: 'none' }}>
        <Sparkles size={120} color="#7b1fa2" />
      </Box>
    </Paper>
  );
}
